import Dexie from 'dexie';
import type { Table } from 'dexie';
import type { Conversation, Message, Section } from '@volt/shared';

/**
 * Offline cache. Conversations and their messages are mirrored into IndexedDB so
 * history renders instantly and remains readable with no connection; the
 * `outbox` table queues messages composed while offline for later flushing.
 */

export interface OutboxItem {
  localId: string;
  conversationId?: string;
  section: Section;
  modelId: string;
  text: string;
  attachmentIds?: string[];
  createdAt: string;
}

class VoltDatabase extends Dexie {
  conversations!: Table<Conversation, string>;
  messages!: Table<Message, string>;
  outbox!: Table<OutboxItem, string>;

  constructor() {
    super('volt');
    this.version(1).stores({
      conversations: 'id, section, updatedAt, lastMessageAt',
      messages: 'id, conversationId, createdAt',
      outbox: 'localId, createdAt',
    });
  }
}

export const db = new VoltDatabase();

export async function cacheConversations(conversations: Conversation[]): Promise<void> {
  try {
    await db.conversations.bulkPut(conversations);
  } catch (error) {
    console.warn('Failed to cache conversations', error);
  }
}

/**
 * Make the cache for `section` mirror a complete fresh list: rows the server no
 * longer returns (deleted elsewhere, moved section) are dropped, the rest upserted.
 */
export async function replaceCachedConversations(
  section: Section,
  conversations: Conversation[],
): Promise<void> {
  try {
    const keep = new Set(conversations.map((item) => item.id));
    await db.transaction('rw', db.conversations, async () => {
      const stale = (await db.conversations.where('section').equals(section).primaryKeys()).filter(
        (id) => !keep.has(id),
      );
      if (stale.length > 0) await db.conversations.bulkDelete(stale);
      await db.conversations.bulkPut(conversations);
    });
  } catch (error) {
    console.warn('Failed to replace cached conversations', error);
  }
}

/** Drop one conversation (and its cached messages) after it was deleted on the server. */
export async function removeCachedConversation(conversationId: string): Promise<void> {
  try {
    await db.conversations.delete(conversationId);
    await db.messages.where('conversationId').equals(conversationId).delete();
  } catch {
    // ignore.
  }
}

export async function readCachedConversations(section?: Section): Promise<Conversation[]> {
  try {
    const all = section
      ? await db.conversations.where('section').equals(section).toArray()
      : await db.conversations.toArray();
    return all.sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt));
  } catch {
    return [];
  }
}

export async function cacheMessages(messages: Message[]): Promise<void> {
  if (messages.length === 0) return;
  try {
    await db.messages.bulkPut(messages);
  } catch (error) {
    console.warn('Failed to cache messages', error);
  }
}

export async function readCachedMessages(conversationId: string): Promise<Message[]> {
  try {
    const list = await db.messages.where('conversationId').equals(conversationId).toArray();
    return list.sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
  } catch {
    return [];
  }
}

export async function enqueueOutbox(item: OutboxItem): Promise<void> {
  await db.outbox.put(item);
}

export async function readOutbox(): Promise<OutboxItem[]> {
  try {
    const list = await db.outbox.toArray();
    return list.sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
  } catch {
    return [];
  }
}

export async function removeOutbox(localId: string): Promise<void> {
  try {
    await db.outbox.delete(localId);
  } catch {
    // ignore.
  }
}

export async function clearCache(): Promise<void> {
  try {
    await Promise.all([db.conversations.clear(), db.messages.clear(), db.outbox.clear()]);
  } catch {
    // ignore.
  }
}
